import React, {Component} from 'react';
import PropTypes from 'prop-types';
import {connect} from 'react-redux';
import {
    devicesAddNew,
    knownDevicesFetchData,
    submitDevices,
    triggerGetDevicesFromDb
} from "../state/action/deviceActions";
import Icon, {CLOUD_UPLOAD, FILTER, LOADING} from "./Icon";
import '../style/General.css';
import '../style/DeviceOverview.css';
import {KNOWN_DEVICES_FETCH_DATA, REQUEST_PENDING, SUBMIT_DEVICE_DATA} from "../constant/reducerConstants";
import {isEditing, isFiltering, sortByEpId} from "../util/DeviceUtils";
import DeviceDisplay from './DeviceDisplay';
import {ST_BLUE_COLOR} from './App';
import {errorListClear, infoListClear} from '../state/action/alertActions';
import {BLANK_MAC_VALUE} from "../constant/appConstants";

const FILTERS = {
    editingDevices: 'editing-devices',
    none: 'none'
};

export class DevicesOverview extends Component {
    state = {
        displayFilters: false,
        filterSetting: FILTERS.none
    };

    componentDidMount() {
        this.props.updateKnownDevices();
        this.props.hydrateState(this.props.siteId);
    };

    handleSubmitDevices = () => {
        if (this.props.devicesUpdating !== REQUEST_PENDING) {
            const devices = this.props.devices;
            this.props.submitDevices(Object.values(devices).filter(isEditing));
        }
    };

    toggleFilter = () => {
        if (this.state.filterSetting === FILTERS.none) {
            this.setState({filterSetting: FILTERS.editingDevices});
        } else {
            this.setState({filterSetting: FILTERS.none});
        }
    };

    isReplaceableDevice = (device, devices) => {
        return !this.checkDeviceHasBlankMacAddress(device)
            && this.numberOfDeviceAppearancesWithSameEpNum(device.equipDetailsId, devices) === 2
            && this.numberOfDevicesWithSameEpNumAndExistMacAddress(device.epNumber, devices) === 1;
    }

    showDeviceDisplayItem = (id, devices) => {
        return this.numberOfDeviceAppearancesWithSameEpNum(id, devices) !== 2
                    || (this.numberOfDeviceAppearancesWithSameEpNum(id, devices) === 2
                     && !this.checkDeviceHasBlankMacAddress(devices[id]));
    }

    filterDeviceList = (filterSetting) => {
        const devices = this.props.devices;
        switch (filterSetting) {
            case FILTERS.editingDevices: {
                return Object.values(devices)
                    .filter(device => {
                        const isReplaceableDevice = this.isReplaceableDevice(device, devices);
                        return isFiltering(device, isReplaceableDevice, devices);
                    })
                    .reduce((acc, dev) => {
                        const deviceItem = {
                            id: dev.equipDetailsId,
                            epNumber: dev.epNumber
                        }
                        acc.push(deviceItem);
                        return acc;
                    }, [])
                    .sort((device1, device2) => {
                        return sortByEpId(devices[device1.id], devices[device2.id]);
                    });
            }
            default:
                return Object.values(devices)
                    .reduce((acc, dev) => {
                        const deviceItem = {
                            id: dev.equipDetailsId,
                            epNumber: dev.epNumber
                        }
                        acc.push(deviceItem);
                        return acc;
                    }, [])
                    .sort((device1, device2) => {
                        return sortByEpId(devices[device1.id], devices[device2.id]);
                    });
        }
    };

    numberOfDeviceAppearancesWithSameEpNum = (id, devices) => {
        let deviceWithDeviceId = devices[id];
        let countDuplicateDeviceWithSameEPNumber = 0;
        if(deviceWithDeviceId.epNumber !== undefined){ // Only apply to device that have epNumber i.e ST600 & ST601
            Object.keys(devices).forEach(key => {
                if (devices[key].epNumber === deviceWithDeviceId.epNumber) {
                    countDuplicateDeviceWithSameEPNumber++;
                }
            });
        }

        return countDuplicateDeviceWithSameEPNumber;
    }

    checkDeviceHasBlankMacAddress = (device) => {
        return device.mac === BLANK_MAC_VALUE;
    }

    numberOfDevicesWithSameEpNumAndExistMacAddress = (epNumber, devices) => {
        let countDuplicateDeviceWithSameEPNumberAndExistMacAddress = 0;
        Object.keys(devices).forEach(key => {
            if (devices[key].epNumber === epNumber && !this.checkDeviceHasBlankMacAddress(devices[key])) {
                countDuplicateDeviceWithSameEPNumberAndExistMacAddress++;
            }
        });
        return countDuplicateDeviceWithSameEPNumberAndExistMacAddress;
    }

    countTotalDevicesShown = (devices) => {
        let totalDeviceListLength = Object.keys(devices).length;
        Object.keys(devices).forEach(id => {
            if (!this.showDeviceDisplayItem(id, devices)) {
                totalDeviceListLength--;
            }
        });
        return totalDeviceListLength;
    }

    getDeviceIdWithSameEpNumberAndBlankMacAddress = (epNumber, devices) => {
        let result = null;
        Object.keys(devices).forEach(key => {
            if (devices[key].epNumber === epNumber && this.checkDeviceHasBlankMacAddress(devices[key])) {
                result = key;
                return false;
            }

            return true;
        });

        return result;
    }

    renderDeviceDisplayItem = (deviceItem, allDevices) => {
        const id = deviceItem.id;
        const deviceWithDeviceId = allDevices[id];
        const isReplaceableDevice = this.isReplaceableDevice(deviceWithDeviceId, allDevices);
        const showDeviceDisplayItem = this.showDeviceDisplayItem(id, allDevices);
        const replacementDeviceId = isReplaceableDevice ? this.getDeviceIdWithSameEpNumberAndBlankMacAddress(deviceWithDeviceId.epNumber, allDevices) : null;
        return (
            showDeviceDisplayItem &&
            <DeviceDisplay
                deviceId={id}
                isReplaceableDevice={isReplaceableDevice}
                replacementDeviceId={replacementDeviceId}
            />
        );
    }

    render() {
        const deviceList = this.filterDeviceList(this.state.filterSetting);
        let totalDeviceListLength = undefined;
        let filteredDeviceListLength = undefined;
        const devices = this.props.devices;

        if (this.state.filterSetting !== FILTERS.none) {
            totalDeviceListLength = this.countTotalDevicesShown(devices)
            filteredDeviceListLength = [...new Set(deviceList.map(d => d.epNumber))].length;
        }

        return (
            <div>
                <div className={'card'}>
                    <div style={{padding: '10px 0 10px 10px'}}>
                        Site: {this.props.siteId}
                        <br/>
                        Task: {this.props.taskId}
                    </div>
                </div>
                <div className={'device_overview-header-wrapper'}>
                    <div>
                        {/* TODO: Add device action*/}
                        {/*<button*/}
                        {/*onClick={() => this.props.handleAddNewDevice(this.props.siteId)}>*/}
                        {/*<Icon iconName={ADD}/>*/}
                        {/*</button>*/}
                        {
                            this.props.devicesUpdating === REQUEST_PENDING ?
                                <button>
                                    <Icon iconName={LOADING} spin={true}/>
                                </button>
                                :
                                <button onClick={this.handleSubmitDevices}>
                                    <Icon iconName={CLOUD_UPLOAD}/>
                                </button>
                        }
                    </div>
                    <div className={'device_overview-header-text'}>
                        {
                            this.props.devicesLoading === REQUEST_PENDING &&
                            <div className={'device_overview-devices-loading'}>
                                Devices loading
                                <Icon
                                    spin={true}
                                    fill={'black'}
                                    iconName={LOADING}/>
                                <div className={'device_overview-separator-dark'}/>
                            </div>
                        }
                        {
                            this.state.filterSetting !== FILTERS.none &&
                            <React.Fragment>
                                {filteredDeviceListLength} / {totalDeviceListLength}
                                <div className={'device_overview-separator-dark'}/>
                            </React.Fragment>
                        }
                        <button
                            id={'filter-toggle-list'}
                            className={this.state.filterSetting === FILTERS.none ?
                                'device_overview-filter-button' :
                                'device_overview-filter-button-invoked'
                            }
                            onClick={this.toggleFilter}>
                            <Icon iconName={FILTER}
                                  fill={this.state.filterSetting !== FILTERS.none ? ST_BLUE_COLOR : undefined}/>
                        </button>
                    </div>
                </div>
                <React.Fragment>
                    {
                        deviceList.map(deviceItem => this.renderDeviceDisplayItem(deviceItem, devices))
                    }
                </React.Fragment>
            </div>
        );
    }
}

DevicesOverview.propTypes = {
    siteId: PropTypes.string,
    taskId: PropTypes.string,
    devices: PropTypes.object,
    devicesLoading: PropTypes.string,
    devicesUpdating: PropTypes.string
};

const mapStateToProps = state => {
    const {network, device, user} = state;
    return {
        devicesUpdating: network[SUBMIT_DEVICE_DATA],
        devicesLoading: network[KNOWN_DEVICES_FETCH_DATA],
        siteId: user.siteId,
        taskId: user.taskId,
        devices: {...device}
    };
};

const mapDispatchToProps = (dispatch, ownProps) => {
    return {
        hydrateState: (siteId) => {
            triggerGetDevicesFromDb(siteId)(dispatch);
        },
        updateKnownDevices: () => {
            knownDevicesFetchData()(dispatch);
        },
        handleAddNewDevice: (siteId) => {
            dispatch(devicesAddNew(siteId));
        },
        submitDevices: (devices) => {
            dispatch(errorListClear());
            dispatch(infoListClear());
            submitDevices(devices)(dispatch);
        }
    };
};

export default connect(mapStateToProps, mapDispatchToProps)(DevicesOverview);



// WEBPACK FOOTER //
// ./src/component/DevicesOverview.jsx